/**
 * Button design specs can be found here https://www.figma.com/design/JlH8b82EaLnGuRMDWGCHs4jF/2.-Buttons?node-id=0-1&p=f&t=SSxVvwrCDzNjVAgK-0
 */

import * as React from 'react'
import { Slot } from 'radix-ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold cursor-pointer transition-colors disabled:cursor-default disabled:opacity-50 aria-disabled:cursor-default aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive focus-visible:outline-2 focus-visible:outline-solid outline-offset-1 outline-primary",
  {
    variants: {
      variant: {
        default:
          'border border-border bg-primary text-background hover:bg-primary/90 focus-visible:bg-primary/90',
        destructive:
          'bg-destructive text-background hover:bg-destructive/90 focus-visible:bg-destructive/90 dark:bg-destructive/60',
        destructiveSecondary:
          'border border-1 border-destructive text-destructive hover:bg-destructive/10',
        secondary:
          'border border-primary text-primary bg-background hover:bg-muted focus-visible:bg-muted dark:hover:bg-input/50',
        tertiary:
          'text-secondary-foreground bg-muted hover:bg-secondary focus-visible:bg-secondary border-1 dark:bg-background dark:hover:bg-secondary/10',
        tertiaryDropdown: 'hover:bg-secondary focus-visible:bg-secondary',
        link: 'text-primary underline-offset-2 hover:text-primary-hover hover:underline focus-visible:text-primary-hover focus-visible:underline border border-transparent ',
        icon: 'text-secondary-foreground hover:text-foreground focus-visible:text-foreground',
        iconPrimary: 'text-primary hover:text-primary focus-visible:text-primary',
        wizard:
          'rounded-[3px] border-2 border-black/20 bg-white text-black/62 hover:bg-white focus-visible:bg-white enabled:hover:border-primary enabled:hover:text-primary enabled:focus-visible:border-primary enabled:focus-visible:text-primary',
      },
      size: {
        default: 'h-9 px-3 has-[>svg]:px-3',
        sm: 'h-7 text-xs gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-11 px-4 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  onClick,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : 'button'

  // `aria-disabled` keeps a button focusable (unlike native `disabled`) but it
  // must not perform its action. Neutralize the click here so consumers don't
  // have to repeat an `if (disabled) return` guard in every handler. This also
  // blocks the native default (form submit / link navigation via `asChild`)
  // and stops the event bubbling, matching how a real `disabled` button behaves.
  const isAriaDisabled = props['aria-disabled'] === true || props['aria-disabled'] === 'true'
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = event => {
    if (isAriaDisabled) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    onClick?.(event)
  }

  return (
    <Comp
      data-slot='button'
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={handleClick}
      {...props}
    />
  )
}

export { Button, buttonVariants }
